import React, { useState } from 'react'
import {
  User, Buildings, ChartLineUp, Globe, Lightbulb, Crosshair,
  ChatCircleText, Binoculars, UsersThree, Article, Pulse,
  CaretDown, CaretRight, Code,
} from '@phosphor-icons/react'

/** Strings the enrichment LLM emits that mean "no data" — treated as empty and hidden. */
const EMPTY_STRINGS = new Set(['', 'unknown', 'n/a', 'none', 'not available', 'not found', 'null'])

const isEmpty = (v: any): boolean => {
  if (v === null || v === undefined) return true
  if (typeof v === 'string') return EMPTY_STRINGS.has(v.trim().toLowerCase())
  if (Array.isArray(v)) return v.length === 0 || v.every(isEmpty)
  if (typeof v === 'object') return Object.values(v).every(isEmpty)
  return false
}

const humanize = (key: string) =>
  key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())

/** Keys used as the heading of an object card, in priority order. */
const HEADING_KEYS = ['headline', 'title', 'name', 'pain', 'gap', 'topic', 'competitor', 'company', 'tool', 'type', 'description']

const isUrl = (v: string) => /^https?:\/\//i.test(v.trim())

const ScalarValue: React.FC<{ value: string | number | boolean }> = ({ value }) => {
  const text = String(value)
  if (typeof value === 'string' && isUrl(value)) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline break-all"
      >
        {text}
      </a>
    )
  }
  return <span className="text-foreground break-words">{text}</span>
}

const ScoreBadge: React.FC<{ value: number }> = ({ value }) => {
  const tone = value >= 7
    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
    : value >= 4
      ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
      : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-emphasis ${tone}`}>
      {value}/10
    </span>
  )
}

const Tags: React.FC<{ items: string[] }> = ({ items }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.filter(t => !isEmpty(t)).map((t, i) => (
      <span
        key={i}
        className="px-2 py-0.5 rounded-md bg-muted text-foreground text-[11px] font-medium border border-border/50"
      >
        {t}
      </span>
    ))}
  </div>
)

/** Salary estimate gets a dedicated compact rendering ("USD 68K – 123K"). */
const SalaryValue: React.FC<{ salary: Record<string, any> }> = ({ salary }) => {
  const fmt = (n: number) => n >= 1000 ? `${Math.round(n / 1000)}K` : String(n)
  return (
    <div>
      <span className="text-foreground font-medium">
        {salary.currency || 'USD'} {fmt(salary.low || 0)} – {fmt(salary.high || 0)}
        {salary.seniority_band && !isEmpty(salary.seniority_band) && (
          <span className="text-muted-foreground font-normal"> · {salary.seniority_band}</span>
        )}
      </span>
      {salary.source && !isEmpty(salary.source) && (
        <p className="text-[10px] text-muted-foreground mt-0.5">{salary.source}</p>
      )}
    </div>
  )
}

/** One object from a list (news item, career entry, pain point, ...) as a mini card. */
const ObjectCard: React.FC<{ obj: Record<string, any> }> = ({ obj }) => {
  const headingKey = HEADING_KEYS.find(k => !isEmpty(obj[k]))
  const rest = Object.entries(obj).filter(([k, v]) => k !== headingKey && !isEmpty(v))
  return (
    <div className="rounded-lg border border-border/50 bg-background px-3 py-2">
      {headingKey && (
        <p className="text-xs font-medium text-foreground leading-snug">{String(obj[headingKey])}</p>
      )}
      {rest.length > 0 && (
        <div className={`space-y-0.5 ${headingKey ? 'mt-1' : ''}`}>
          {rest.map(([k, v]) => (
            <div key={k} className="flex gap-1.5 text-[11px] leading-snug">
              <span className="text-muted-foreground shrink-0">{humanize(k)}:</span>
              <ValueRenderer name={k} value={v} compact />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** Recursive renderer that picks the right presentation for any value shape. */
const ValueRenderer: React.FC<{ name: string; value: any; compact?: boolean }> = ({ name, value, compact }) => {
  if (typeof value === 'number' && /score$/.test(name) && value >= 1 && value <= 10) {
    return <ScoreBadge value={value} />
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return <ScalarValue value={value} />
  }
  if (Array.isArray(value)) {
    const items = value.filter(v => !isEmpty(v))
    if (items.length === 0) return null
    if (items.every(v => typeof v === 'string' || typeof v === 'number')) {
      return <Tags items={items.map(String)} />
    }
    return (
      <div className="space-y-1.5 w-full">
        {items.map((v, i) =>
          typeof v === 'object' ? <ObjectCard key={i} obj={v} /> : <ScalarValue key={i} value={v} />
        )}
      </div>
    )
  }
  if (typeof value === 'object') {
    if (name === 'salary_estimate') return <SalaryValue salary={value} />
    return <FieldList obj={value} compact={compact} />
  }
  return <ScalarValue value={String(value)} />
}

/** Label + value rows for an object's non-empty fields. */
const FieldList: React.FC<{ obj: Record<string, any>; compact?: boolean }> = ({ obj, compact }) => {
  const entries = Object.entries(obj).filter(([, v]) => !isEmpty(v))
  if (entries.length === 0) return null
  return (
    <div className={compact ? 'space-y-0.5' : 'space-y-2'}>
      {entries.map(([k, v]) => {
        const isBlock = Array.isArray(v) || (typeof v === 'object' && v !== null)
        return (
          <div key={k} className={isBlock ? 'space-y-1' : 'flex gap-2 items-baseline'}>
            <span className="text-[11px] font-medium text-muted-foreground shrink-0">
              {humanize(k)}
            </span>
            <div className="text-xs min-w-0">
              <ValueRenderer name={k} value={v} compact={compact} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

interface SectionDef {
  key: string
  title: string
  icon: React.ReactNode
  defaultOpen?: boolean
}

const SECTIONS: SectionDef[] = [
  { key: 'person', title: 'Person', icon: <User weight="duotone" />, defaultOpen: true },
  { key: 'company', title: 'Company', icon: <Buildings weight="duotone" />, defaultOpen: true },
  { key: 'sector', title: 'Sector', icon: <ChartLineUp weight="duotone" /> },
  { key: 'economy', title: 'Economy', icon: <Globe weight="duotone" /> },
  { key: 'outreach_hooks', title: 'Outreach Hooks', icon: <Lightbulb weight="duotone" /> },
  { key: 'buying_signals', title: 'Buying Signals', icon: <Crosshair weight="duotone" /> },
  { key: 'communication_prefs', title: 'Communication Preferences', icon: <ChatCircleText weight="duotone" /> },
  { key: 'competitive_intel', title: 'Competitive Intel', icon: <Binoculars weight="duotone" /> },
  { key: 'relationship_map', title: 'Relationship Map', icon: <UsersThree weight="duotone" /> },
  { key: 'content_relevance', title: 'Content Relevance', icon: <Article weight="duotone" /> },
  { key: 'company_signals', title: 'Company Signals', icon: <Pulse weight="duotone" /> },
]

const Section: React.FC<{ def: SectionDef; data: Record<string, any> }> = ({ def, data }) => {
  const [open, setOpen] = useState(!!def.defaultOpen)
  const fieldCount = Object.values(data).filter(v => !isEmpty(v)).length
  return (
    <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full px-3.5 py-2.5 flex items-center gap-2.5 text-left hover:bg-muted/40 transition-colors"
      >
        <span className="w-6 h-6 rounded-md bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.14] flex items-center justify-center text-[#5e6ad2] dark:text-[#a5b4fc] [&>svg]:w-3.5 [&>svg]:h-3.5">
          {def.icon}
        </span>
        <span className="text-xs font-emphasis text-foreground flex-1">{def.title}</span>
        <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
          {fieldCount} {fieldCount === 1 ? 'field' : 'fields'}
        </span>
        {open ? <CaretDown className="w-3.5 h-3.5 text-muted-foreground" /> : <CaretRight className="w-3.5 h-3.5 text-muted-foreground" />}
      </button>
      {open && (
        <div className="px-3.5 pb-3 pt-1 border-t border-border/40">
          <FieldList obj={data} />
        </div>
      )}
    </div>
  )
}

interface EnrichmentResultViewProps {
  data: Record<string, any>
  provider?: string
  model?: string
  status?: string
}

/**
 * Structured display of a contact's enrichment_data payload (see backend
 * app/services/enrichment/schemas.py EnrichmentResult). Empty / "Unknown"
 * fields are hidden; a raw-JSON toggle remains as escape hatch.
 */
export const EnrichmentResultView: React.FC<EnrichmentResultViewProps> = ({ data, provider, model, status }) => {
  const [showRaw, setShowRaw] = useState(false)

  const knownKeys = new Set(SECTIONS.map(s => s.key))
  // Any top-level keys the schema doesn't cover yet still get a generic section.
  const extraSections: SectionDef[] = Object.keys(data)
    .filter(k => !knownKeys.has(k) && k !== 'metadata' && typeof data[k] === 'object' && data[k] !== null && !Array.isArray(data[k]) && !isEmpty(data[k]))
    .map(k => ({ key: k, title: humanize(k), icon: <Article weight="duotone" /> }))

  const sections = [...SECTIONS, ...extraSections].filter(
    s => data[s.key] && typeof data[s.key] === 'object' && !isEmpty(data[s.key])
  )

  const meta = data.metadata || {}
  const chips = [
    provider && `Provider: ${provider}`,
    model && `Model: ${model}`,
    status && `Status: ${status}`,
    meta.confidence_score != null && `Confidence: ${Math.round(meta.confidence_score * 100)}%`,
    meta.sources_used != null && `${meta.sources_used} sources`,
    meta.depth && !isEmpty(meta.depth) && `Depth: ${meta.depth}`,
  ].filter(Boolean) as string[]

  return (
    <div className="space-y-2.5">
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((c, i) => (
            <span key={i} className="px-2 py-0.5 rounded-md bg-background border border-border/50 text-[10px] font-medium text-muted-foreground">
              {c}
            </span>
          ))}
        </div>
      )}

      {sections.length === 0 ? (
        <p className="text-xs text-muted-foreground">No enrichment data was returned for this contact.</p>
      ) : (
        sections.map(s => <Section key={s.key} def={s} data={data[s.key]} />)
      )}

      <button
        type="button"
        onClick={() => setShowRaw(r => !r)}
        className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        <Code className="w-3.5 h-3.5" />
        {showRaw ? 'Hide raw JSON' : 'View raw JSON'}
      </button>
      {showRaw && (
        <pre className="p-3 bg-card border border-border/50 rounded-lg overflow-x-auto text-[11px] text-muted-foreground whitespace-pre-wrap max-h-96 overflow-y-auto">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  )
}
